import { Pencil, Trash2, MessageSquare, ShieldCheck, ShieldX } from 'lucide-react';
import type { User } from '../../types/user';
import { formatPhone } from '../../types/api';
import { DataTable, Badge, EmptyState } from '../ui';

interface UserAccountListProps {
  accounts: User[];
  onEdit: (account: User) => void;
  onDelete: (account: User) => void;
}

function UserAccountList({ accounts, onEdit, onDelete }: UserAccountListProps) {
  const columns = [
    {
      header: 'Usuário',
      accessorKey: 'username' as const,
    },
    {
      header: 'Nome',
      accessorKey: 'name' as const,
    },
    {
      header: 'Telefone',
      accessorKey: 'phone' as const,
      cell: (row: User) => (row.phone ? formatPhone(row.phone) : '-'),
    },
    {
      header: 'Email',
      accessorKey: 'email' as const,
      cell: (row: User) => row.email || '-',
    },
    {
      header: 'Chave Integração',
      accessorKey: 'integration_key' as const,
      cell: (row: User) => row.integration_key ? '••••••••' : '-',
    },
    {
      header: 'Notificações',
      accessorKey: 'notifications' as const,
      cell: (row: User) => {
        const notifications = [];
        if (row.notify_fallbackresponse) notifications.push('Fallback');
        if (row.notify_queue_limit_timeout) notifications.push('Fila');
        if (row.notify_alerts) notifications.push('Alertas');
        if (row.notify_alerts_resume) notifications.push('Resumo');
        return notifications.length > 0 ? notifications.join(', ') : '-';
      },
    },
    {
      header: '2FA',
      accessorKey: 'two_factor_enabled' as const,
      cell: (row: User) =>
        row.two_factor_enabled ? (
          <Badge variant="success">
            <ShieldCheck className="h-3 w-3 mr-1 inline" />
            Ativado
          </Badge>
        ) : (
          <Badge variant="default">
            <ShieldX className="h-3 w-3 mr-1 inline" />
            Desativado
          </Badge>
        ),
    },
    {
      header: 'Status',
      accessorKey: 'active' as const,
      cell: (row: User) => (
        <Badge variant={row.active ? 'success' : 'error'}>{row.active ? 'Ativo' : 'Inativo'}</Badge>
      ),
    },
  ];

  if (accounts.length === 0) {
    return (
      <EmptyState
        icon={<MessageSquare className="h-12 w-12" />}
        title={'Nenhum usuário disponível'}
        description={'Crie seu primeiro usuário para começar'}
      />
    );
  }

  return <DataTable columns={columns} data={accounts} onEdit={onEdit} onDelete={onDelete} />;
}

export default UserAccountList;
